import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { map } from 'rxjs/operators';
import { DefaultSupervisionService } from '../../../services/default-supervision.service';
import {
  DefaultSupervisionContact,
  DefaultSupervisionMetrikTypeEnum,
  DefaultSupervisionResolvedLine,
} from '../../../models/default-supervision.model';
import { getAvatarColor, getInitials } from '../../../utils/avatar.util';
import {
  selectSupervisionLine,
  SupervisionEstablishmentRef,
  supervisionProvenanceLabel,
  toSupervisionResolvePayload,
} from '../../../utils/default-supervision.util';

/**
 * Restitution du « Référent en charge de la supervision » d'une Metrik (PRED-3061, PRED-3236).
 * Composant partagé entre les fiches bâtiment et équipement, UN SEUL superviseur affiché :
 * - si un superviseur a été SAISI MANUELLEMENT sur la Metrik (`manualContact`), il est
 *   affiché tel quel (il s'applique à la Metrik entière, quel que soit l'établissement) ;
 * - sinon, le superviseur par défaut est résolu dynamiquement pour chaque établissement et
 *   la ligne retenue est celle de l'établissement gestionnaire (`isMain`), à défaut celle du
 *   premier établissement rattaché (règle partagée `selectSupervisionLine`).
 * La provenance (organisation / établissement gestionnaire / bâtiment ou équipement) est
 * affichée au survol du nom. Le résultat par défaut n'est pas persisté : il est recalculé à
 * chaque affichage.
 */
@Component({
  selector: 'default-supervision-resolved',
  templateUrl: './default-supervision-resolved.component.html',
})
export class DefaultSupervisionResolvedComponent implements OnChanges {
  @Input() metrikType: DefaultSupervisionMetrikTypeEnum;
  @Input() genericNodeId?: string;
  /** Établissements rattachés, dans l'ordre de rattachement ; `isMain` = établissement gestionnaire. */
  @Input() establishments?: SupervisionEstablishmentRef[];
  /** Superviseur saisi manuellement sur la Metrik ; prioritaire sur le défaut résolu. */
  @Input() manualContact?: DefaultSupervisionContact;

  /** Ligne retenue parmi celles résolues par le back (une par établissement). */
  line?: DefaultSupervisionResolvedLine;
  loading = false;

  constructor(private readonly defaultSupervisionService: DefaultSupervisionService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.metrikType || changes.genericNodeId || changes.establishments || changes.manualContact) {
      this.load();
    }
  }

  private load(): void {
    // Contact saisi manuellement : il prime, aucune résolution du défaut nécessaire.
    if (this.manualContact?._id) {
      this.line = undefined;
      this.loading = false;
      return;
    }
    if (!this.metrikType || !this.genericNodeId) {
      this.line = undefined;
      return;
    }
    this.loading = true;
    this.defaultSupervisionService
      .resolveLines(this.metrikType, this.genericNodeId, toSupervisionResolvePayload(this.establishments))
      .pipe(map(response => response.data ?? []))
      .subscribe({
        next: lines => {
          this.line = selectSupervisionLine(lines, this.establishments);
          this.loading = false;
        },
        error: () => {
          this.line = undefined;
          this.loading = false;
        },
      });
  }

  contactName(contact: DefaultSupervisionContact): string {
    return `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();
  }

  contactInitials(contact: DefaultSupervisionContact): string {
    return getInitials(contact.firstName, contact.lastName);
  }

  avatarColor(contact: DefaultSupervisionContact): string {
    return getAvatarColor(contact._id);
  }

  /** Provenance du superviseur saisi manuellement (« au niveau du bâtiment » / « de l'équipement »). */
  get manualProvenanceLabel(): string {
    return supervisionProvenanceLabel('manual', this.metrikType);
  }

  /** Provenance de la ligne résolue (organisation / établissement gestionnaire), « Non couvert » sans contact. */
  provenanceLabel(line: DefaultSupervisionResolvedLine): string {
    return supervisionProvenanceLabel(line.contact ? line.source : undefined, this.metrikType);
  }
}
